import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

interface FilteredNewsletterQueryArgs {
	enabled?: boolean;
	excludeExpired?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	industry?: Api.Industry;
	search?: string;
	templateType?: Api.TemplateType;
	meta?: LevStandardQueryMeta;
}

const BASE_QUERY_KEY = '{industryName}/htmlNewsletter/filter';

export const useGetNewsletterTemplatesByKeyword = ({
	enabled,
	excludeExpired = true,
	impersonationContext,
	industry,
	search,
	templateType = Api.TemplateType.HtmlNewsletter,
	meta,
}: FilteredNewsletterQueryArgs) => {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		queryFn: async () => {
			return userSession.webServiceHelper.callAsync<Api.ITemplate[]>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { search, excludeExpired, industryName: industry, templateType },
					urlPath: `template/${encodeURIComponent(industry)}/htmlNewsletter/filter`,
					impersonationContext,
				}),
				'GET',
				null
			);
		},
		queryKey: [BASE_QUERY_KEY, search, industry, templateType, impersonationContext],
		meta,
	});
};

export const invalidateGetNewsletterTemplatesByKeyword = ({
	impersonationContext,
	industry,
	templateType,
	search,
}: FilteredNewsletterQueryArgs) => {
	if (search !== undefined) {
		// Invalidate specific search query
		reactQueryClient.invalidateQueries({
			queryKey: [BASE_QUERY_KEY, search, industry, templateType, impersonationContext],
		});
	} else {
		// Invalidate all search queries for this industry/template type
		reactQueryClient.invalidateQueries({
			queryKey: [BASE_QUERY_KEY],
			predicate: query => {
				const [baseKey, , queryIndustry, queryTemplateType, queryImpersonation] = query.queryKey;
				return (
					baseKey === BASE_QUERY_KEY &&
					queryIndustry === industry &&
					queryTemplateType === templateType &&
					queryImpersonation === impersonationContext
				);
			},
		});
	}
};
